import { __decorate } from "tslib";
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { elements } from './sdb-elements.js';
/**
 * Mainmenu element, used to wrap mainmenu-item elements
 *
 * @slot - Unnamed slot (default)
 * @slot - usermenu slot, used for icons in upper bar
 * @slot - search slot, used for search icon in main menu bar

 *
 * @prop {String} logo - Optional, used to define a logo in the upper left corner
 *
 */
let SDBNavMainMenu = class SDBNavMainMenu extends LitElement {
    constructor() {
        super(...arguments);
        this.logo = '';
        this.opened = false;
    }
    render() {
        return html `
            <section id="white" part="topbar"></section>
            <section id="primary" part="main">
                <svg viewBox="0 0 500 150" preserveAspectRatio="none">
                    <path
                        d="M0,100 C150,300 450,0 450,0 L500,00 L0,0 Z"
                        style="stroke: none;fill: var(--theme-secondary);"
                    ></path>
                </svg>
            </section>

            <section class="wrapper">
                <section class="appbar">
                    ${this.logo
            ? html `<img
                              part="logo"
                              src="${this.logo}"
                              class="logo"
                          />`
            : html ``}
                </section>
                <header class="header">
                    <input
                        class="menu-btn"
                        type="checkbox"
                        id="menu-btn"
                        @change=${this._checkChange}
                    />
                    <label class="menu-icon" for="menu-btn">
                        <span class="navicon"></span>
                    </label>
                    <ul class="menu">
                        <div class="search"><slot name="search"></slot></div>
                        <slot class="usermenu" name="usermenu"></slot>
                        <slot class="menuItems" ?opened=${this.opened}></slot>
                    </ul>
                </header>
            </section>
        `;
    }
    _checkChange(e) {
        this.opened = e.target.checked;
    }
};
SDBNavMainMenu.styles = [
    elements,
    css `
            /* -------------------------------------------
   ALL STYLING BELOW IS MOBILE FIRST
------------------------------------------- */

            :host {
                display: grid;
                grid-template-columns: 1fr;
                grid-template-rows: 70px;
                grid-template-areas:
                    'appbar'
                    'mainmenu';
                background: var(--theme-primary);
            }

            #white {
                grid-area: appbar;
                display: block;
                width: 100%;
                height: 70px;
                background: white;
                position: absolute;
            }

            #primary {
                display: none; /* entire main menu is hidden on mobile */
            }

            .search {
                display: none;
            }

            /* -------------------------------------------
   WRAPPER - USED TO KEEP ITEMS CENTERED
------------------------------------------- */

            section.wrapper {
                display: block;
                max-width: 1400px;
                width: 100%;
                height: 70px;
                margin: 0 auto;
                position: relative;
            }

            /* -------------------------------------------
   APPBAR - WHITE, LOGO, USERMENU
------------------------------------------- */

            section.appbar {
                display: block;
                height: 70px;
                max-width: 1400px;
                margin: 0 auto;
                padding: 0 15px;
            }

            section.appbar img /* logo */ {
                margin-top: 15px;
                height: 40px;
                float: left;
                object-fit: contain;
                object-position: 0;
                width: 150px;
            }

            /* -------------------------------------------
   MAINMENU - HEADER, BUTTON, ITEMS
------------------------------------------- */

            .header {
                width: 100%;
                height: 0;
            }

            .header .menu {
                clear: both;
                height: 0;
                transition: height 0.3s ease-out;
                line-height: 50px;
                width: auto;
                margin: 0;
                padding: 0;
                list-style: none;
                overflow: hidden;
                background: var(--theme-primary)
                    /* only used on mobile, transparent on desktop */;
            }

            .header .menu-icon /* hamburger menu icon */ {
                cursor: pointer;
                display: block;
                position: absolute;
                top: 10px;
                right: 0;
                padding: 24px 20px;
                user-select: none;
                z-index: 11;
            }

            .header .menu-icon .navicon /* hamburger menu icon part */ {
                background: #023850;
                display: block;
                height: 2px;
                transition: background 0.2s ease-out;
                width: 24px;
                position: relative;
            }

            .header .menu-icon .navicon:before,
            .header .menu-icon .navicon:after {
                background: #023850;
                content: '';
                display: block;
                height: 100%;
                position: absolute;
                transition: rotate 0.2s ease-out;
                width: 100%;
            }

            .header .menu-icon .navicon:before {
                top: 7px;
            }

            .header .menu-icon .navicon:after {
                top: -7px;
            }

            .header .menu-btn /* hidden checkbox for hamburger menu icon */ {
                display: none;
            }

            .header .menu-btn:checked ~ .menu /* displays menu items if hamburger menu is clicked */ {
                height: 100vh;
            }

            .usermenu {
                display: block;
                background: white;
                width: 100%;
                background: white;
            }

            .header .menu-btn:checked ~ .menu-icon .navicon {
                background: transparent;
            }

            .header .menu-btn:checked ~ .menu-icon .navicon:before {
                transform: rotate(-45deg);
            }

            .header .menu-btn:checked ~ .menu-icon .navicon:after {
                transform: rotate(45deg);
            }

            .header .menu-btn:checked ~ .menu-icon:not(.steps) .navicon:before,
            .header .menu-btn:checked ~ .menu-icon:not(.steps) .navicon:after {
                top: 0;
            }

            /* -------------------------------------------
   ALL STYLING BELOW IS NON-MOBILE
------------------------------------------- */

            @media only screen and (min-width: 768px) {
                section.appbar img {
                    left: 0;
                    width: auto;
                }

                .header {
                    height: 70px;
                    max-width: 1400px;
                    margin: 0 auto;
                }

                .header .menu {
                    line-height: 70px;
                    height: 70px;
                    clear: none;
                    max-height: none;
                    box-shadow: none;
                    padding: 0 15px;
                    background: transparent;
                }

                .menuItems {
                    margin-top: 0;
                }

                .header .menu-icon {
                    display: none;
                }

                .usermenu {
                    position: absolute;
                    top: 0;
                    right: 15px;
                    display: block;
                    width: auto;
                    height: 70px;
                    line-height: 70px;
                }

                #primary {
                    grid-area: mainmenu;
                    display: inline-block;
                    width: 100%;
                    height: 70px;
                    background: var(--theme-primary);
                    vertical-align: middle;
                    overflow: hidden;
                    contain: content;
                }

                svg {
                    display: inline-block;
                    position: absolute;
                    top: 0;
                    left: 0;
                    height: 100%;
                    width: 100%;
                    /* SR: needed to constrain the svg to the parent's box in Safari <= 14 */
                    position: relative;
                }

                .search {
                    display: flex;
                    width: 70px;
                    height: 70px;
                    float: right;
                    justify-content: center;
                    align-items: center;
                }
            }
        `,
];
__decorate([
    property({ type: String })
], SDBNavMainMenu.prototype, "logo", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SDBNavMainMenu.prototype, "opened", void 0);
SDBNavMainMenu = __decorate([
    customElement('sdb-nav-mainmenu')
], SDBNavMainMenu);
export { SDBNavMainMenu };
//# sourceMappingURL=sdb-nav-mainmenu.js.map